import React from 'react';
import {
  Building2, Star, MapPin, ChevronRight,
  FileText, RefreshCw, BadgeCheck
} from 'lucide-react';
import { Contract } from '../../types';
import { useContractActions } from '../../hooks/useContractActions';
import { useAuth } from '../../contexts/AuthContext';
import { SetAsideIcon } from '../SetAsideIcon';
import { FitBadge } from '../ui/FitBadge';
import { STAGE_META } from '../../lib/pipeline-service';
import { UrgencyTag } from '../ui/UrgencyTag';
import { EvidenceChip, EvidenceState } from '../ui/EvidenceChip';

interface ModernContractCardProps {
  contract: Contract;
  onSelect: (contract: Contract) => void;
  hasProposal?: boolean;
  hasActiveSession?: boolean;
  /** Highlight when this card's detail panel is open. */
  selected?: boolean;
  /** Show the "AI-verified" pill when an AI deep-score has been cached. */
  aiVerified?: boolean;
  variant?: 'card' | 'compact' | 'row';
}

/**
 * Derive 1–2 inline Evidence Chips ("why this matched") from the contract and
 * the user's profile. Grounded when the profile actually backs the claim;
 * inferred / gap otherwise — never asserted blindly.
 */
function useEvidence(contract: Contract): { state: EvidenceState; label: string }[] {
  const { profile } = useAuth();
  const chips: { state: EvidenceState; label: string }[] = [];

  const naics = contract.naicsCode;
  const profileNaics: string[] = (profile as any)?.naics_codes || [];
  if (naics) {
    if (profileNaics.includes(naics)) {
      chips.push({ state: 'grounded', label: `NAICS ${naics} match` });
    } else if (profileNaics.some((c) => c.slice(0, 4) === naics.slice(0, 4))) {
      chips.push({ state: 'inferred', label: `NAICS ${naics} related` });
    } else {
      chips.push({ state: 'gap', label: `NAICS ${naics}` });
    }
  }

  const sa = contract.setAsides?.[0];
  const profileAsides: string[] = (profile as any)?.qualified_set_asides || [];
  if (sa) {
    chips.push({
      state: profileAsides.includes(sa) ? 'grounded' : 'inferred',
      label: `${sa} set-aside`,
    });
  }

  return chips.slice(0, 2);
}

function ResumeDraftPills({ hasActiveSession, hasProposal }: { hasActiveSession?: boolean; hasProposal?: boolean }) {
  return (
    <>
      {hasActiveSession && (
        <span className="px-2 py-0.5 bg-fit-moderate-bg text-fit-moderate border border-fit-moderate-border text-[10px] font-semibold rounded-full inline-flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Resume
        </span>
      )}
      {hasProposal && (
        <span className="px-2 py-0.5 bg-bw-primary-tint text-bw-primary border border-bw-primary/20 text-[10px] font-semibold rounded-full inline-flex items-center gap-1">
          <FileText className="h-3 w-3" /> Draft
        </span>
      )}
    </>
  );
}

function AiVerifiedPill() {
  return (
    <span className="px-2 py-0.5 bg-evidence-grounded-bg text-evidence-grounded border border-evidence-grounded-border text-[10px] font-bold rounded-full inline-flex items-center gap-1">
      <BadgeCheck className="h-3 w-3" /> AI-verified
    </span>
  );
}

export function ModernContractCard({
  contract,
  onSelect,
  hasProposal,
  hasActiveSession,
  selected,
  aiVerified,
  variant = 'card',
}: ModernContractCardProps) {
  const { loading, toggleFavorite } = useContractActions();
  const evidence = useEvidence(contract);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onSelect(contract);
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!loading) await toggleFavorite(contract.id);
  };

  const placeState =
    typeof contract.placeOfPerformance?.state === 'object'
      ? (contract.placeOfPerformance?.state as any)?.name
      : contract.placeOfPerformance?.state;

  // Compact Row variant (desktop list view)
  if (variant === 'row') {
    return (
      <div
        onClick={handleClick}
        className={`group bg-bw-surface border-b border-bw-border hover:bg-bw-primary-tint/50 transition-colors cursor-pointer ${
          contract.isFavorited ? 'bg-fit-moderate-bg/30' : ''
        }`}
      >
        <div className="flex items-center gap-4 px-4 py-3">
          <FitBadge score={contract.matchScore || 0} numberSize={18} aiScored={contract.aiScored} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-bw-ink truncate group-hover:text-bw-primary transition-colors">
                {contract.title}
              </h3>
              {aiVerified && <AiVerifiedPill />}
              <ResumeDraftPills hasActiveSession={hasActiveSession} hasProposal={hasProposal} />
            </div>
            <div className="flex items-center gap-4 text-sm text-bw-muted">
              <span className="flex items-center gap-1 truncate max-w-[220px]">
                <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                {contract.agency}
              </span>
              <span className="font-mono text-xs px-1.5 py-0.5 bg-bw-surface-subtle rounded">
                {contract.naicsCode}
              </span>
              {placeState && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {placeState}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-5 flex-shrink-0">
            <UrgencyTag dueDate={contract.dueDate} />
            <button
              onClick={handleFavoriteClick}
              disabled={loading}
              aria-label={contract.isFavorited ? 'Remove from saved' : 'Save contract'}
              className={`p-2 rounded-lg transition-colors ${
                contract.isFavorited
                  ? 'text-fit-moderate-stroke hover:bg-fit-moderate-bg'
                  : 'text-bw-muted hover:bg-bw-surface-subtle'
              }`}
            >
              <Star className="h-5 w-5" fill={contract.isFavorited ? 'currentColor' : 'none'} />
            </button>
            <ChevronRight className="h-5 w-5 text-bw-muted group-hover:text-bw-primary transition-colors" />
          </div>
        </div>
      </div>
    );
  }

  // Compact variant (mobile list view)
  if (variant === 'compact') {
    return (
      <div
        onClick={handleClick}
        className={`bg-bw-surface rounded-xl border p-4 active:bg-bw-surface-subtle transition-colors cursor-pointer ${
          contract.isFavorited ? 'border-fit-moderate-border bg-fit-moderate-bg/30' : 'border-bw-border'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FitBadge score={contract.matchScore || 0} />
            {aiVerified && <AiVerifiedPill />}
          </div>
          <UrgencyTag dueDate={contract.dueDate} />
        </div>
        <h3 className="font-semibold text-bw-ink line-clamp-2 text-sm mb-1 leading-snug">{contract.title}</h3>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-xs text-bw-muted truncate">{contract.agency}</span>
          <span className="font-mono text-[10px] px-1.5 py-0.5 bg-bw-surface-subtle text-bw-muted rounded">
            {contract.naicsCode}
          </span>
        </div>
        {evidence.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {evidence.map((e, i) => (
              <EvidenceChip key={i} state={e.state}>{e.label}</EvidenceChip>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mt-2">
          <ResumeDraftPills hasActiveSession={hasActiveSession} hasProposal={hasProposal} />
        </div>
      </div>
    );
  }

  // Default card variant (the comp's "best fit" card)
  return (
    <div
      onClick={handleClick}
      className={`group bg-bw-surface rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden shadow-card hover:shadow-card-hover ${
        selected
          ? 'border-bw-primary ring-2 ring-bw-primary/20'
          : contract.isFavorited
          ? 'border-fit-moderate-border'
          : 'border-bw-border hover:border-bw-primary/50'
      }`}
    >
      <div className="p-3.5">
        {/* Top row: fit + verification / state · urgency */}
        <div className="flex items-center justify-between mb-2.5 gap-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <FitBadge score={contract.matchScore || 0} />
            {aiVerified && <AiVerifiedPill />}
            {contract.pipelineStage && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${STAGE_META[contract.pipelineStage].pill}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${STAGE_META[contract.pipelineStage].dot}`} />
                {STAGE_META[contract.pipelineStage].label}
              </span>
            )}
            <ResumeDraftPills hasActiveSession={hasActiveSession} hasProposal={hasProposal} />
          </div>
          <UrgencyTag dueDate={contract.dueDate} className="flex-shrink-0" />
        </div>

        {/* Title */}
        <h3 className="font-semibold text-[13px] text-bw-ink line-clamp-2 leading-snug mb-2 group-hover:text-bw-primary transition-colors">
          {contract.title}
        </h3>

        {/* Agency + NAICS + location */}
        <div className="flex items-center gap-2 flex-wrap mb-2.5">
          <span className="text-[11px] text-bw-muted truncate max-w-[200px] inline-flex items-center gap-1">
            <Building2 className="h-3 w-3 flex-shrink-0" />
            {contract.agency}
          </span>
          <span className="font-mono text-[10px] px-1.5 py-0.5 bg-bw-surface-subtle text-bw-muted rounded">
            {contract.naicsCode}
          </span>
          {contract.setAsides && contract.setAsides.length > 0 && (
            <span className="flex items-center gap-1">
              {contract.setAsides.slice(0, 2).map((s) => (
                <SetAsideIcon key={s} type={s} size={18} />
              ))}
            </span>
          )}
        </div>

        {/* Evidence chips — "why this matched" */}
        {evidence.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {evidence.map((e, i) => (
              <EvidenceChip key={i} state={e.state}>{e.label}</EvidenceChip>
            ))}
          </div>
        )}

        {/* AI capture-analyst read — the "why it fits" + top opportunity/blocker, brought
            from the AI Best Matches view onto the browse card when AI-scored. */}
        {contract.aiScored && (contract.aiRationale || contract.aiOpportunities?.length || contract.aiBlockers?.length) && (
          <div className="mt-2.5 pt-2.5 border-t border-bw-border/60 space-y-1">
            {contract.aiRationale && (
              <p className="text-[11px] text-bw-ink/80 line-clamp-2 leading-snug">{contract.aiRationale}</p>
            )}
            {contract.aiOpportunities?.slice(0, 1).map((o, i) => (
              <p key={`o${i}`} className="text-[11px] text-success flex items-start gap-1">
                <span className="flex-shrink-0">✓</span><span className="line-clamp-1">{o}</span>
              </p>
            ))}
            {contract.aiBlockers?.slice(0, 1).map((b, i) => (
              <p key={`b${i}`} className="text-[11px] text-urgent flex items-start gap-1">
                <span className="flex-shrink-0">⚠</span><span className="line-clamp-1">{b}</span>
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-bw-surface-subtle border-t border-bw-border">
        <button
          onClick={handleFavoriteClick}
          disabled={loading}
          aria-label={contract.isFavorited ? 'Remove from saved' : 'Save contract'}
          className={`p-1.5 rounded-lg transition-all ${
            contract.isFavorited
              ? 'text-fit-moderate-stroke bg-fit-moderate-bg'
              : 'text-bw-muted hover:bg-bw-border/50'
          }`}
        >
          <Star className="h-[18px] w-[18px]" fill={contract.isFavorited ? 'currentColor' : 'none'} />
        </button>
        <div className="flex items-center gap-1 text-[12px] font-semibold text-bw-primary group-hover:gap-2 transition-all">
          <span>View Details</span>
          <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </div>
  );
}
